import { ClaudePresenceChip } from "@/components/claude/ClaudePresence";
import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/format";
import { deptsForRole, deptOfPath, DEPARTMENTS } from "@/lib/departments";
import CommandPalette from "@/components/CommandPalette";
import AssistantPanel from "@/components/AssistantPanel";
import { WinCelebration } from "@/components/WinCelebration";
import BrandLogo from "@/components/BrandLogo";
import {
  Briefcase, Calculator, HardHat, BarChart3, Settings, ChevronDown, CheckSquare,
  Search, LogOut, Command as CommandIcon, Plus, Menu, X, KeyRound, Star, AtSign, Layers, FileText, Sun, Moon, RefreshCw, MessageSquare, PenLine, Plane,
} from "lucide-react";
import SignatureSetupDialog from "@/components/SignatureSetupDialog";
import OutOfOfficeDialog from "@/components/OutOfOfficeDialog";
import { LiveReadsDot } from "@/components/LiveReads";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/context/ThemeContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { triggerEmailSync } from "@/hooks/useLiveSync";

const DEPT_ICON = { bd: Briefcase, estimating: Calculator, approvals: CheckSquare, operations: HardHat, insights: BarChart3, admin: Settings };
const PATH_LABEL = Object.fromEntries(DEPARTMENTS.flatMap((d) => d.items.map((i) => [i.to, i.label])));

export default function AppShell({ children }) {
  const { user, logout, refresh } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Procore-style: focusing a field selects its whole contents so typing replaces it.
  // Double-click still lets you edit within. Opt out per-field with data-no-select-all.
  useEffect(() => {
    const onFocusIn = (e) => {
      const el = e.target;
      if (!el || el.tagName !== "INPUT") return;
      const t = (el.type || "text").toLowerCase();
      if (!["text", "number", "search", "tel", "url", "email"].includes(t)) return;
      if (el.dataset.noSelectAll != null || el.readOnly || el.disabled) return;
      try { el.select(); } catch { /* some browsers can't select number inputs */ }
    };
    document.addEventListener("focusin", onFocusIn);
    return () => document.removeEventListener("focusin", onFocusIn);
  }, []);
  const [mobileNav, setMobileNav] = useState(false);
  const [openDept, setOpenDept] = useState(null);
  const [userMenu, setUserMenu] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [oooOpen, setOooOpen] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [curPw, setCurPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [mentions, setMentions] = useState([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [msgUnread, setMsgUnread] = useState(0);
  const [gq, setGq] = useState("");
  const [gres, setGres] = useState(null);
  const [gopen, setGopen] = useState(false);
  const gtimer = useRef(null);
  const runGlobal = (v) => {
    setGq(v); setGopen(true);
    if (gtimer.current) clearTimeout(gtimer.current);
    if (v.trim().length < 2) { setGres(null); return; }
    gtimer.current = setTimeout(async () => {
      try { const { data } = await api.get("/search", { params: { q: v } }); setGres(data.results || []); }
      catch { setGres([]); }
    }, 300);
  };
  const goResult = (r) => {
    const term = gq;
    setGopen(false); setGq(""); setGres(null);
    if (r.type === "sheet") navigate(`/bids/${r.bid_id}?sheet=${r.sheet_id}&q=${encodeURIComponent(term)}`);
    else navigate(`/bids/${r.bid_id}?tab=documents`);
  };
  const closeTimer = useRef(null);
  const userMenuRef = useRef(null);
  const mentionRef = useRef(null);
  // Close dropdowns on an outside click (NOT on mouse-leave — that killed the menu
  // the moment you moved down toward "Settings").
  useEffect(() => {
    const onDown = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenu(false);
      if (mentionRef.current && !mentionRef.current.contains(e.target)) setMentionOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);
  const unreadMentions = mentions.filter((m) => m.unread).length;

  const seenMentions = React.useRef(null);
  const loadMentions = () => api.get("/mentions").then((r) => {
    const ms = r.data.mentions || [];
    setMentions(ms);
    if (seenMentions.current === null) {
      seenMentions.current = new Set(ms.map((m) => m.comment_id));
    } else {
      ms.filter((m) => m.unread && !seenMentions.current.has(m.comment_id)).reverse().forEach((m) => {
        toast(`${m.author_name} mentioned you`, {
          description: `${m.label ? "in " + m.label + " — " : ""}${(m.text || "").slice(0, 90)}`,
          action: { label: "View", onClick: () => goMention(m) },
        });
      });
      ms.forEach((m) => seenMentions.current.add(m.comment_id));
    }
  }).catch(() => {});
  React.useEffect(() => {
    loadMentions();
    const t = setInterval(loadMentions, 15000);
    return () => clearInterval(t);
  }, []);
  const seenMsgs = React.useRef(null);
  const loadMsgUnread = () => api.get("/messaging/unread").then((r) => {
    setMsgUnread(r.data.total || 0);
    const recent = r.data.recent || [];
    if (seenMsgs.current === null) {
      seenMsgs.current = new Set(recent.map((m) => m.message_id));
    } else {
      const onConvo = location.pathname === "/messages";
      const curC = new URLSearchParams(location.search).get("c");
      recent.filter((m) => !seenMsgs.current.has(m.message_id)).reverse().forEach((m) => {
        if (!(onConvo && curC === m.channel_id)) {
          toast(`${m.author_name} messaged you`, {
            description: (m.text || "").slice(0, 90),
            action: { label: "Open", onClick: () => navigate(m.link) },
          });
        }
      });
      recent.forEach((m) => seenMsgs.current.add(m.message_id));
    }
  }).catch(() => {});
  React.useEffect(() => {
    loadMsgUnread();
    const t = setInterval(loadMsgUnread, 15000);
    return () => clearInterval(t);
  }, [location.pathname]);
  const openMentions = () => {
    setMentionOpen((o) => !o);
    if (!mentionOpen && unreadMentions) {
      api.post("/mentions/seen").catch(() => {});
      setMentions((ms) => ms.map((m) => ({ ...m, unread: false })));
    }
  };
  const goMention = (m) => {
    setMentionOpen(false);
    if (m.link) { try { const u = new URL(m.link); navigate(u.pathname + u.search); return; } catch { /* fall through */ } }
    navigate(m.entity_type === "bid" ? `/bids/${m.entity_id}` : "/clients");
  };

  const depts = deptsForRole(user?.role);
  const activeDept = deptOfPath(user?.role, location.pathname);
  const accessible = new Set(depts.flatMap((d) => d.items.map((i) => i.to)));
  const favs = (user?.favorites || []).filter((p) => accessible.has(p));
  const canFav = accessible.has(location.pathname);
  const isFav = favs.includes(location.pathname);
  const toggleFav = async () => {
    const cur = user?.favorites || [];
    const next = cur.includes(location.pathname) ? cur.filter((p) => p !== location.pathname) : [...cur, location.pathname];
    try { await api.post("/auth/favorites", { favorites: next }); refresh?.(); }
    catch { toast.error("Could not update favorites"); }
  };

  const submitPw = async () => {
    if (newPw.length < 8) { toast.error("Use at least 8 characters."); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { new_password: newPw, current_password: curPw || undefined });
      toast.success("Password saved. You can now sign in with your email and password.");
      setPwOpen(false); setNewPw(""); setCurPw("");
      refresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save password"); }
    finally { setSavingPw(false); }
  };

  React.useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setPaletteOpen((o) => !o); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const initials = (user?.name || user?.email || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const enterDept = (key) => { clearTimeout(closeTimer.current); setOpenDept(key); };
  const leaveDept = () => { closeTimer.current = setTimeout(() => setOpenDept(null), 150); };
  const [approvalCount, setApprovalCount] = useState(0);
  const [draftCount, setDraftCount] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = () => {
      api.get("/approvals/inbox").then((r) => { if (alive) setApprovalCount((r.data || []).length); }).catch(() => {});
      api.get("/email/drafts/count").then((r) => { if (alive) setDraftCount(r.data.count || 0); }).catch(() => {});
    };
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [location.pathname]);

  // ── Constantly-fresh inbox ──────────────────────────────────────────────
  // While the app is open we pull new mail every 60s and broadcast so every live
  // view (Context, Today, Inbox, client rollups) reloads in near-real-time. The
  // 15-min platform cron stays as the safety net for when the app is closed.
  const [gmailConnected, setGmailConnected] = useState(false);
  const [syncing, setSyncing] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get("/email/status").then((r) => { if (alive) setGmailConnected(!!r.data?.connected); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    if (!gmailConnected) return;
    triggerEmailSync();
    const id = setInterval(() => triggerEmailSync(), 60000);
    return () => clearInterval(id);
  }, [gmailConnected]);
  const syncNow = async () => {
    setSyncing(true);
    toast.success("Syncing your inbox — new items appear in a moment");
    await triggerEmailSync(5000);
    setTimeout(() => setSyncing(false), 5000);
  };

  const go = (to) => { setOpenDept(null); setMobileNav(false); navigate(to); };

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-slate-50">
      {/* Top bar */}
      <header className="h-16 shrink-0 bg-[#000000] flex items-center gap-2 px-3 sm:px-5 relative z-40">
        <button className="text-slate-300 lg:hidden" onClick={() => setMobileNav(true)} data-testid="open-mobile-nav"><Menu className="w-5 h-5" /></button>
        <div className="shrink-0"><BrandLogo variant="light" size={22} /></div>

        {/* Department mega-menu (desktop) */}
        <nav className="hidden lg:flex items-center h-full ml-2 2xl:ml-4" data-testid="dept-nav">
          {depts.map((d) => {
            const Icon = DEPT_ICON[d.key] || Briefcase;
            const isActive = activeDept === d.key;
            const isOpen = openDept === d.key;
            return (
              <div key={d.key} className="relative h-full" onMouseEnter={() => enterDept(d.key)} onMouseLeave={leaveDept}>
                <button
                  data-testid={`dept-${d.key}`}
                  onClick={() => go(d.items[0].to)}
                  className={`h-full px-2 2xl:px-4 flex items-center gap-1.5 text-[13px] 2xl:text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
                    isActive ? "text-white border-[#F4C400]" : "text-slate-300 hover:text-white border-transparent"
                  }`}
                >
                  <Icon className={`w-4 h-4 hidden 2xl:block ${isActive ? "text-[#F4C400]" : ""}`} />
                  {d.label}
                  {d.key === "approvals" && approvalCount > 0 && (
                    <span data-testid="approvals-nav-badge" title={`${approvalCount} waiting on you`}
                      className="ml-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#F4C400] text-black text-[10px] font-bold grid place-items-center leading-none">
                      {approvalCount > 9 ? "9+" : approvalCount}
                    </span>
                  )}
                  <ChevronDown className="w-3 h-3 opacity-60" />
                </button>
                {isOpen && (
                  <div data-testid={`dept-menu-${d.key}`} className="absolute left-0 top-full w-72 bg-white rounded-b-lg shadow-xl border border-slate-200 py-2 z-50">
                    {d.items.map((it) => (
                      <button key={it.to} data-testid={it.testid} onClick={() => go(it.to)}
                        className={`w-full text-left px-4 py-2.5 hover:bg-slate-50 transition ${location.pathname.startsWith(it.to) ? "bg-slate-50" : ""}`}>
                        <div className="text-sm font-medium text-black flex items-center gap-1.5">
                          {it.label}
                          {it.to === "/email-agent" && draftCount > 0 && (
                            <span data-testid="inbox-drafts-nav-badge" title={`${draftCount} email draft${draftCount === 1 ? "" : "s"} waiting for review`}
                              className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#F4C400] text-black text-[10px] font-bold grid place-items-center leading-none">
                              {draftCount > 9 ? "9+" : draftCount}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-400">{it.desc}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="flex-1" />

        <button data-testid="open-command-palette" onClick={() => setPaletteOpen(true)}
          className="hidden sm:flex items-center gap-2 h-9 px-2.5 rounded-md border border-white/15 text-slate-300 hover:border-white/30 text-sm w-9 xl:w-40 2xl:w-64 shrink-0" title="Search (Ctrl/Cmd K)">
          <Search className="w-4 h-4 shrink-0" />
          <span className="flex-1 text-left truncate hidden xl:inline">Search…</span>
          <kbd className="hidden xl:flex items-center gap-0.5 text-[11px] font-mono bg-white/10 rounded px-1.5 py-0.5"><CommandIcon className="w-3 h-3" />K</kbd>
        </button>

        <ClaudePresenceChip />
        {!location.pathname.startsWith("/pipeline") && <Button data-testid="header-new-bid" onClick={() => navigate("/pipeline?new=1")}
          className="h-9 bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold text-sm shrink-0">
          <Plus className="w-4 h-4 xl:mr-1.5" /> <span className="hidden xl:inline">New lead</span>
        </Button>}

        {canFav && (
          <button data-testid="fav-toggle" title={isFav ? "Unpin from favorites" : "Pin to favorites"} onClick={toggleFav}
            className="hidden 2xl:block text-slate-400 hover:text-[#F4C400] shrink-0">
            <Star className={`w-5 h-5 ${isFav ? "fill-[#F4C400] text-[#F4C400]" : ""}`} />
          </button>
        )}

        {/* Light / Dark theme toggle */}
        <button
          data-testid="theme-toggle"
          onClick={toggleTheme}
          title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          className="hidden 2xl:block text-slate-300 hover:text-[#F4C400] p-1.5 shrink-0 transition-colors"
        >
          {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>

        {/* Live email sync — instant on-demand pull (auto-syncs every 60s in the background).
            Hidden on the crowded mobile header; phones stay fresh via the 60s auto-sync + the Inbox "Sync now". */}
        {gmailConnected && (
          <button data-testid="global-sync-now" onClick={syncNow} disabled={syncing}
            title={syncing ? "Syncing your inbox…" : "Sync email now"}
            className="hidden 2xl:inline-flex items-center text-slate-300 hover:text-white p-1.5 shrink-0 transition-colors disabled:opacity-70">
            <RefreshCw className={`w-5 h-5 ${syncing ? "animate-spin text-[#F4C400]" : ""}`} />
          </button>
        )}

        {/* Messages */}
        <button data-testid="messages-nav" onClick={() => navigate("/messages")}
          title="Team messages" className="relative text-slate-300 hover:text-white p-1.5 shrink-0 transition-colors">
          <MessageSquare className="w-5 h-5" />
          {msgUnread > 0 && (
            <span data-testid="messages-nav-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#F4C400] text-black text-[10px] font-bold grid place-items-center">{msgUnread > 9 ? "9+" : msgUnread}</span>
          )}
        </button>

        {/* Mentions */}
        <div className="relative shrink-0" ref={mentionRef}>
          <LiveReadsDot />
          <button data-testid="mention-bell" onClick={openMentions} className="relative text-slate-300 hover:text-white p-1.5">
            <AtSign className="w-5 h-5" />
            {unreadMentions > 0 && (
              <span data-testid="mention-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#F4C400] text-black text-[10px] font-bold grid place-items-center">{unreadMentions > 9 ? "9+" : unreadMentions}</span>
            )}
          </button>
          {mentionOpen && (
            <div className="absolute right-0 top-full mt-1 w-80 bg-white rounded-lg shadow-xl border border-slate-200 py-1 z-50" data-testid="mention-dropdown">
              <div className="px-4 py-2 border-b border-slate-100 text-xs font-semibold uppercase tracking-wider text-slate-500">Mentions &amp; follow-ups</div>
              {mentions.length === 0 && <div className="px-4 py-4 text-xs text-slate-400">No mentions yet.</div>}
              <div className="max-h-80 overflow-auto">
                {mentions.slice(0, 8).map((m) => (
                  <button key={m.comment_id} onClick={() => goMention(m)} data-testid={`mention-item-${m.comment_id}`}
                    className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-start gap-2">
                    <span className="w-6 h-6 rounded-full bg-[#003E87]/10 text-[#003E87] grid place-items-center text-[10px] font-semibold shrink-0 mt-0.5">{(m.author_name || "?").slice(0, 2).toUpperCase()}</span>
                    <span className="min-w-0"><span className="text-sm text-black block truncate"><b>{m.author_name}</b> · {m.label}</span><span className="text-xs text-slate-500 block truncate">{m.text}</span></span>
                  </button>
                ))}
              </div>
              <button onClick={() => { setMentionOpen(false); navigate("/today"); }} className="w-full text-center px-4 py-2 text-xs text-[#003E87] hover:bg-slate-50 border-t border-slate-100" data-testid="mention-viewall">View all in Today</button>
            </div>
          )}
        </div>

        {/* User menu */}
        <div className="relative shrink-0" ref={userMenuRef}>
          <button data-testid="user-menu-btn" onClick={() => setUserMenu((o) => !o)} className="flex items-center relative">
            <Avatar className="w-8 h-8"><AvatarImage src={user?.picture} /><AvatarFallback className="bg-[#003E87] text-white text-xs">{initials}</AvatarFallback></Avatar>
            {user?.out_of_office?.active && <span data-testid="avatar-ooo-dot" title={`Out of office until ${user.out_of_office.until}`} className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-amber-400 border-2 border-black" />}
          </button>
          {userMenu && (
            <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-xl border border-slate-200 py-2 z-50">
              <div className="px-4 py-2 border-b border-slate-100">
                <div className="text-sm font-medium text-black truncate">{user?.name || user?.email}</div>
                <div className="text-xs text-slate-400">{ROLE_LABELS[user?.role] || user?.role}</div>
              </div>
              <button data-testid="settings-menu-item" onClick={() => { navigate("/settings"); setUserMenu(false); }} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"><Settings className="w-4 h-4 text-slate-400" /> Settings</button>
              <button data-testid="theme-menu-item" onClick={() => { toggleTheme(); setUserMenu(false); }} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 2xl:hidden">{theme === "dark" ? <Sun className="w-4 h-4 text-slate-400" /> : <Moon className="w-4 h-4 text-slate-400" />} {theme === "dark" ? "Light mode" : "Dark mode"}</button>
              <button data-testid="ooo-menu-item" onClick={() => { setOooOpen(true); setUserMenu(false); }} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"><Plane className="w-4 h-4 text-slate-400" /> {user?.out_of_office?.active ? `Out of office · back ${user.out_of_office.until}` : "Out of office"}</button>
              <button data-testid="signature-menu-item" onClick={() => { setSigOpen(true); setUserMenu(false); }} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"><PenLine className="w-4 h-4 text-slate-400" /> My signature &amp; stamp</button>
              <button data-testid="set-password-button" onClick={() => { setPwOpen(true); setUserMenu(false); }} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"><KeyRound className="w-4 h-4 text-slate-400" /> Set / change password</button>
              <button data-testid="logout-button" onClick={logout} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"><LogOut className="w-4 h-4 text-slate-400" /> Sign out</button>
            </div>
          )}
        </div>
      </header>

      {favs.length > 0 && (
        <div className="hidden lg:flex items-center gap-1 h-9 px-5 bg-white border-b border-slate-200 shrink-0" data-testid="favorites-bar">
          <Star className="w-3.5 h-3.5 text-[#F4C400] mr-1 fill-[#F4C400]" />
          {favs.map((p) => (
            <button key={p} data-testid={`fav-${p}`} onClick={() => navigate(p)}
              className={`text-xs px-2.5 py-1 rounded transition ${location.pathname === p ? "bg-slate-100 text-black font-medium" : "text-slate-500 hover:text-black hover:bg-slate-50"}`}>
              {PATH_LABEL[p] || p}
            </button>
          ))}
        </div>
      )}

      {/* Mobile drawer */}
      {mobileNav && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setMobileNav(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-[#000000] flex flex-col">
            <div className="h-16 flex items-center gap-2 px-5 border-b border-white/10">
              <BrandLogo variant="light" size={22} />
              <button className="ml-auto text-slate-400" onClick={() => setMobileNav(false)}><X className="w-4 h-4" /></button>
            </div>
            <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-4">
              {depts.map((d) => (
                <div key={d.key}>
                  <div className="px-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{d.label}</div>
                  {d.items.map((it) => (
                    <NavLink key={it.to} to={it.to} data-testid={`m-${it.testid}`} onClick={() => setMobileNav(false)}
                      className={({ isActive }) => `flex items-center justify-between px-3 py-2 rounded-md text-sm ${isActive ? "bg-[#F4C400]/10 text-white font-medium" : "text-slate-300 hover:bg-white/5"}`}>
                      <span>{it.label}</span>
                      {it.to === "/approvals" && approvalCount > 0 && (
                        <span data-testid="m-approvals-nav-badge" className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#F4C400] text-black text-[10px] font-bold grid place-items-center leading-none">{approvalCount > 9 ? "9+" : approvalCount}</span>
                      )}
                      {it.to === "/email-agent" && draftCount > 0 && (
                        <span data-testid="m-inbox-drafts-nav-badge" className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#F4C400] text-black text-[10px] font-bold grid place-items-center leading-none">{draftCount > 9 ? "9+" : draftCount}</span>
                      )}
                    </NavLink>
                  ))}
                </div>
              ))}
            </nav>
          </aside>
        </div>
      )}

      <main className="flex-1 overflow-auto gf-app"><div key={location.pathname} className="page-fade h-full">{children}</div></main>

      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} />

      <SignatureSetupDialog open={sigOpen} onClose={() => setSigOpen(false)} />
      <OutOfOfficeDialog open={oooOpen} onClose={() => setOooOpen(false)} current={user?.out_of_office} onSaved={() => refresh && refresh()} />
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent data-testid="set-password-dialog">
          <DialogHeader>
            <DialogTitle>{user?.has_password ? "Change password" : "Set a password"}</DialogTitle>
            <DialogDescription>
              {user?.has_password ? "Update the password you use for email sign-in." : "Create a password so you can sign in with your email and stay in this browser."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {user?.has_password && (
              <div><Label className="text-xs">Current password</Label>
                <Input data-testid="current-password-input" type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} /></div>
            )}
            <div><Label className="text-xs">New password</Label>
              <Input data-testid="new-password-input" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="At least 8 characters" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)}>Cancel</Button>
            <Button data-testid="save-password-button" onClick={submitPw} disabled={savingPw} className="bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {(window.__gfConnectorMode = !!user?.features?.assistant_claude) ? null : <AssistantPanel />}
      <WinCelebration />
    </div>
  );
}
